import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyMeter, buyElectricity, getUserWallet } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import { icons } from "../../utils/icons";
import BeneficiaryPicker from "../../components/feature/BeneficiaryPicker";
import { useBeneficiaries, saveBeneficiary } from "../../hooks/useBeneficiaries";
import CouponPicker    from "../../components/feature/CouponPicker";
import CashbackToggle      from "../../components/feature/CashbackToggle";
import PaymentConfirmSheet from "../../components/feature/PaymentConfirmSheet";
import MarketingSlides from "../../components/feature/MarketingSlides";
import { PhoneRequiredGate } from "../../components/feature/ProfilePromptBanner";
import { useAnalytics } from "../../hooks/useAnalytics";

// DISCO accent dots — mapped onto the semantic token scale rather than
// one-off hexes, so each dot adapts automatically in dark mode instead
// of staying a fixed (and possibly low-contrast) light-mode color.
// Where a DISCO doesn't cleanly match an existing tone, "muted" is used
// as a safe neutral fallback rather than inventing a new color.
const DISCOS = [
  { id: "EKEDC",  label: "Eko Electric",   short: "EKEDC",  tone: "warning", img: icons.ekedc  },
  { id: "IKEDC",  label: "Ikeja Electric", short: "IKEDC",  tone: "danger",  img: icons.ikedc  },
  { id: "AEDC",   label: "Abuja Electric", short: "AEDC",   tone: "success", img: icons.aedc   },
  { id: "PHEDC",  label: "Port Harcourt",  short: "PHEDC",  tone: "info",    img: icons.phed   },
  { id: "IBEDC",  label: "Ibadan Elect.",  short: "IBEDC",  tone: "purple",  img: icons.ibedc  },
  { id: "EEDC",   label: "Enugu Electric", short: "EEDC",   tone: "success", img: null         },
  { id: "KAEDCO", label: "Kaduna Elect.",  short: "KAEDCO", tone: "warning", img: icons.kaedco },
  { id: "KEDCO",  label: "Kano Electric",  short: "KEDCO",  tone: "success", img: null         },
];

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000];
const METER_MIN_LEN = 10;
const DEBOUNCE_MS   = 800;

export default function ElectricityPage() {
  const navigate            = useNavigate();
  const { state: navState } = useLocation();
  const [searchParams]      = useSearchParams();
  const { user, userLoading = false, userReady }            = useAuth();
  const { mostRecent }      = useBeneficiaries("electricity");
  const { track, conversion } = useAnalytics();

  // Priority: reuse from success page → mostRecent beneficiary → searchParams
  const prev = navState?.reuse || (mostRecent ? {
    disco:       mostRecent.disco,
    meterType:   mostRecent.meterType,
    meterNumber: mostRecent.meterNumber,
  } : null);

  const [disco,        setDisco]        = useState(prev?.disco       || searchParams.get("disco") || "");
  const [meterType,    setMeterType]    = useState(prev?.meterType   || "prepaid");
  const [meterNumber,  setMeterNumber]  = useState(prev?.meterNumber || "");
  const [amount,       setAmount]       = useState("");
  const [verifying,    setVerifying]    = useState(false);
  const [meterInfo,    setMeterInfo]    = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [pinError,     setPinError]     = useState("");
  const [nairaBalance, setNairaBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const debounceTimer = useRef(null);
  const lastVerified  = useRef("");

  // ── Coupon + cashback state ────────────────────────────────
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [cashbackToUse,  setCashbackToUse]  = useState(0);

  const rawAmount  = Number(amount) || 0;
  const minAmount  = meterInfo?.minAmount || 1000;
  const arrears    = meterInfo?.arrears   || 0;
  const couponSaving  = appliedCoupon?.discount || 0;
  const totalSavings  = couponSaving + cashbackToUse;
  const netAmount     = Math.max(rawAmount - totalSavings, 0);

  const isValid    = !!disco && meterNumber.length >= METER_MIN_LEN && rawAmount >= minAmount && !!meterInfo;
  const hasBalance = nairaBalance !== null && nairaBalance >= netAmount;
  const canPay     = isValid && hasBalance;

  useEffect(() => {
      track.viewItem("electricity");
    }, []);

  useEffect(() => {
    getUserWallet()
      .then(r => { if (r.status) setNairaBalance(r.data?.nairaBalance || 0); })
      .catch(() => {})
      .finally(() => setBalanceLoading(false));
  }, []);

  // ── Core verify function ──────────────────────────────────
  const runVerify = useCallback(async (d, m, mt) => {
    if (!d || m.length < METER_MIN_LEN) return;
    const key = `${d}:${m}:${mt}`;
    if (key === lastVerified.current) return;
    setVerifying(true);
    setMeterInfo(null);
    try {
      const res = await verifyMeter(d, m, mt);
      if (res.status) {
        setMeterInfo({
          customerName: res.customerName,
          address:      res.address,
          minAmount:    res.minAmount    || 1000,
          maxAmount:    res.maxAmount    || 100000,
          arrears:      res.arrears      || 0,
          outstanding:  res.outstanding  || 0,
          meterNumber:  res.meterNumber  || m,
        });
        lastVerified.current = key;

      } else {
        toast.error(res.message || "Meter not found");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:     "electricity",
            title:    "Electricity Bill",
            amount:   0,
            txStatus: "processing",
            reference: errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "Electricity Bill failed");
    } finally {
      setVerifying(false);
    }
  }, [amount]);

  // Auto-verify on input change (debounced)
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    if (!disco || meterNumber.length < METER_MIN_LEN) {
      setMeterInfo(null);
      lastVerified.current = "";
      return;
    }
    debounceTimer.current = setTimeout(() => runVerify(disco, meterNumber, meterType), DEBOUNCE_MS);
    return () => clearTimeout(debounceTimer.current);
  }, [disco, meterNumber, meterType, runVerify]);

  // Auto-verify immediately if page opens with a pre-filled number
  useEffect(() => {
    if (disco && meterNumber.length >= METER_MIN_LEN) {
      runVerify(disco, meterNumber, meterType);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Beneficiary selected from dropdown ────────────────────
  const handleBeneficiarySelect = useCallback((b) => {
    // Apply all fields from the saved beneficiary
    if (b.disco     && b.disco     !== disco)     setDisco(b.disco);
    if (b.meterType && b.meterType !== meterType) setMeterType(b.meterType);
    lastVerified.current = ""; // force re-verify
  }, [disco, meterType]);

   const handlePay = () => {
  if (canPay && !loading) {
    track.beginCheckout("electricity", rawAmount); // fire before showing sheet
    setPinError("");
    setShowConfirm(true);
  }
};

  const executePay = async (pin) => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const res = await buyElectricity(
        disco, meterNumber, meterType,
        rawAmount,
        user?.phone,
        appliedCoupon?.couponId || null,
        appliedCoupon?.discount || 0,
        cashbackToUse,
        pin,
      );
      if (res.status) {
        saveBeneficiary({
          id:          meterNumber,
          type:        "electricity",
          meterNumber,
          disco,
          meterType,
          label:       meterInfo?.customerName || meterNumber,
          lastUsed:    Date.now(),
        });
        track.purchase("electricity", netAmount, res.transactionId);
        // If API tells you it's first ever bill payment:
        if (res.isFirstBillPayment) {
          conversion.firstPurchase(netAmount, "electricity");
        }
        navigate("/bills/success", {
          state: {
            type:          "electricity",
            title:         `${disco} ${meterType === "prepaid" ? "Prepaid" : "Postpaid"}`,
            detail:        `Meter: ${meterNumber}`,
            subDetail:     meterInfo?.customerName,
            amount:        netAmount,
            grossAmount:   rawAmount,
            couponSaving,
            cashbackUsed:  cashbackToUse,
            totalSavings,
            couponCode:    appliedCoupon?.code || null,
            requestId:     res.requestId,
            token:         res.token    || null,
            units:         res.units    || null,
            txStatus:      res.txStatus || "completed",   // ← ADD THIS
            transactionId: res.transactionId,
            reuse: { disco, meterNumber, meterType },
          },
        });
      } else {
        toast.error(res.message || "Electricity purchase failed");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:     "electricity",
            title:    "Electricity Bill",
            amount:   0,
            txStatus: "processing",
            reference: errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "Electricity Bill failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Electricity</h1>
              <p className="text-muted text-xs">Pay any DISCO across Nigeria</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-brand-light border border-brand-border px-3 py-1.5 rounded-xl">
            <i className="ri-wallet-3-line text-brand text-xs" />
            <span className="text-brand-text text-xs font-bold">
              {balanceLoading ? "…" : `₦${(nairaBalance || 0).toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      <PhoneRequiredGate user={user} userLoading={userLoading} userReady={userReady}>
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-36 space-y-5">

        {/* Marketing slide */}
        <MarketingSlides page="electricity" compact />

        {/* Meter type */}
        <div className="flex items-center gap-2 bg-surface-hover rounded-2xl p-1">
          {["prepaid", "postpaid"].map(t => (
            <button key={t}
              onClick={() => { setMeterType(t); setMeterInfo(null); lastVerified.current = ""; }}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold capitalize transition-all ${
                meterType === t ? "bg-surface text-brand shadow-card" : "text-muted"
              }`}>
              {t}
            </button>
          ))}
        </div>

        {/* DISCO grid */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Select DISCO</p>
          <div className="grid grid-cols-2 gap-2">
            {DISCOS.map(d => (
              <button key={d.id}
                onClick={() => { setDisco(d.id); setMeterInfo(null); lastVerified.current = ""; }}
                className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                  disco === d.id ? "border-brand bg-brand-light" : "border-border bg-surface"
                }`}>
                <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 bg-surface-muted">
                  {d.img
                    ? <img src={d.img} alt={d.short} className="w-7 h-7 object-contain" />
                    : <i className="ri-flashlight-line text-base" style={{ color: `var(--color-${d.tone})` }} />}
                </div>
                <div>
                  <p className="text-primary text-xs font-bold">{d.short}</p>
                  <p className="text-muted text-[10px] leading-tight">{d.label}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Meter number with BeneficiaryPicker */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Meter Number</p>
          <BeneficiaryPicker
            type="electricity"
            value={meterNumber}
            onChange={v => {
              setMeterNumber(v);
              if (v.length < METER_MIN_LEN) { setMeterInfo(null); lastVerified.current = ""; }
            }}
            onSelect={handleBeneficiarySelect}
            placeholder="Enter meter number"
            numeric
            maxLength={13}
            inputMode="numeric"
            suffix={
              verifying
                ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-warning)", borderTopColor: "transparent" }} />
                : meterInfo
                  ? <i className="ri-checkbox-circle-fill text-brand text-lg" />
                  : null
            }
          />
          {!disco && meterNumber.length > 3 && (
            <p className="text-warning-text text-xs mt-2 px-1">⚠ Please select a DISCO first</p>
          )}

          {/* Verification result card */}
          {meterInfo && (
            <div className="mt-2 px-4 py-3 bg-brand-light rounded-xl border border-brand-border space-y-2">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-brand text-sm" />
                <p className="text-brand-text text-sm font-bold">{meterInfo.customerName}</p>
              </div>
              {meterInfo.address && meterInfo.address !== "N/A" && (
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-brand text-xs mt-0.5 flex-shrink-0" />
                  <p className="text-brand-text text-xs">{meterInfo.address}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="bg-surface rounded-lg px-3 py-2">
                  <p className="text-muted text-[10px]">Min Amount</p>
                  <p className="text-secondary text-xs font-bold">₦{meterInfo.minAmount?.toLocaleString()}</p>
                </div>
                <div className="bg-surface rounded-lg px-3 py-2">
                  <p className="text-muted text-[10px]">Max Amount</p>
                  <p className="text-secondary text-xs font-bold">₦{meterInfo.maxAmount?.toLocaleString()}</p>
                </div>
                <div className="bg-surface rounded-lg px-3 py-2">
                  <p className="text-muted text-[10px]">Customer Arrears</p>
                  <p className="text-secondary text-xs font-bold">₦{meterInfo.arrears?.toLocaleString()}</p>
                </div>
                <div className="bg-surface rounded-lg px-3 py-2">
                  <p className="text-muted text-[10px]">Customer Outstanding</p>
                  <p className="text-secondary text-xs font-bold">₦{meterInfo.outstanding?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Amount */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">
            Amount
            {meterInfo && <span className="text-muted font-normal ml-1">(min ₦{meterInfo.minAmount?.toLocaleString()})</span>}
          </p>
          <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors mb-3">
            <span className="text-muted font-bold mr-2">₦</span>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map(amt => (
              <button key={amt} onClick={() => setAmount(String(amt))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(amt)
                    ? "bg-brand text-on-brand border-brand"
                    : "bg-surface text-secondary border-border-strong hover:border-brand-border"
                }`}>
                ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
              </button>
            ))}
          </div>
        </div>
        {/* Cashback toggle — shown when amount ≥ minAmount and meter verified */}
        {meterInfo && rawAmount >= minAmount && (
          <CashbackToggle
            purchaseAmount={rawAmount}
            appliedAmount={cashbackToUse}
            onApply={setCashbackToUse}
          />
        )}

        {/* Coupon picker */}
        {meterInfo && rawAmount >= minAmount && (
          <div>
            <p className="text-secondary text-sm font-semibold mb-3">Coupon / Discount</p>
            <CouponPicker
              service="electricity"
              amount={rawAmount}
              appliedCoupon={appliedCoupon}
              onApply={setAppliedCoupon}
              onRemove={() => setAppliedCoupon(null)}
            />
          </div>
        )}

        {/* Order summary — shown when any saving is applied */}
        {(appliedCoupon || cashbackToUse > 0) && rawAmount > 0 && (
          <div className="bg-brand-light border border-brand-border rounded-2xl px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted">Electricity amount</span>
              <span className="text-primary font-semibold">₦{rawAmount.toLocaleString()}</span>
            </div>
            {cashbackToUse > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-hand-coin-line text-xs" />Cashback
                </span>
                <span className="text-brand-text font-bold">−₦{cashbackToUse.toLocaleString()}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-coupon-3-line text-xs" />{appliedCoupon.code}
                </span>
                <span className="text-brand-text font-bold">−₦{couponSaving.toLocaleString()}</span>
              </div>
            )}
            <div className="h-px bg-brand-border" />
            <div className="flex justify-between text-sm">
              <span className="text-primary font-bold">You pay</span>
              <span className="text-brand-text font-bold text-base">₦{netAmount.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
      </PhoneRequiredGate>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border px-5 py-4 space-y-2">

        {isValid && !hasBalance && nairaBalance !== null && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger-border rounded-xl">
            <i className="ri-error-warning-line text-danger text-sm flex-shrink-0" />
            <p className="text-danger-text text-xs font-medium flex-1">
              Insufficient balance. <button onClick={() => navigate("/deposit")} className="underline font-semibold">Top up</button>
            </p>
          </div>
        )}
        <button onClick={handlePay} disabled={!canPay || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
            : verifying          ? "Verifying meter…"
            : !disco             ? "Select a DISCO to continue"
            : !meterInfo         ? meterNumber.length >= METER_MIN_LEN ? "Verifying…" : "Enter meter number"
            : rawAmount < minAmount ? `Enter amount (min ₦${minAmount.toLocaleString()})`
            : !hasBalance        ? "Insufficient balance"
            : totalSavings > 0
              ? `Pay ₦${netAmount.toLocaleString()} (saved ₦${totalSavings.toLocaleString()})`
              : `Pay ₦${rawAmount.toLocaleString()}`}
        </button>
      </div>
      <PaymentConfirmSheet
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={executePay}
        loading={loading}
        pinError={pinError}
        amount={rawAmount}
        netAmount={netAmount}
        couponSaving={couponSaving}
        cashbackUsed={cashbackToUse}
        rows={[
          { label: "DISCO",         value: disco },
          { label: "Meter Type",    value: meterType === "prepaid" ? "Prepaid" : "Postpaid" },
          { label: "Meter Number",  value: meterInfo?.meterNumber || meterNumber, mono: true },
          { label: "Account Name",  value: meterInfo?.customerName || "—" },
        ]}
        confirmLabel="Pay Electricity Bill"
      />
    </div>
  );
}
